import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { documentApi } from '../../../../../api/document';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DeleteEntityModal } from '../../../DeleteEntityModal/index';
import { shouldRenderDeleteButton } from '../../RenderLogic';

import { useContext } from 'react';

import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { DocumentOverviewContext } from '../../Context';
import { useFeatureFlagValue } from '../../../../../hooks/useFeatureFlagValue';

type DeleteDocumentProps = {
  renderClickableItem: (onClick: () => void) => JSX.Element;
};

export const DeleteDocumentAction = ({
  renderClickableItem,
}: DeleteDocumentProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument: document } = useContext(DocumentOverviewContext);
  const { setDisabled } = useContext(ButtonGroupContext);

  const navigate = useNavigate();
  const { showToast } = useToastProvider();
  const isNewTemplateUpgradeEnabled = useFeatureFlagValue(
    'new-template-upgrade',
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const onClose = () => setDisabled(false, setIsDeleteModalOpen);
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);

  const modalText = <p>{t('modals.areYouSureDeleteDocument')}</p>;

  const handleDelete = async () => {
    const toastCommonProps = {
      id: 'document-overview-delete-toast',
      replace: true,
    };

    setDisabled(true, setIsDeletingDoc);

    try {
      await documentApi.deleteDocument(document.id);

      setIsDeletingDoc(false);
      setIsDeleteModalOpen(false);

      showToast({
        'data-cy': 'delete-document-action-notify-success-toast',
        status: 'success',
        title: t('notifications.success'),
        description: t('success.successfullyDeletedDocument'),
        ...toastCommonProps,
      });

      void navigate('/workspace');
    } catch (error) {
      setDisabled(false, setIsDeletingDoc);
      setIsDeleteModalOpen(false);

      showToast({
        'data-cy': 'delete-document-action-notify-error-toast',
        status: 'error',
        title: t('notifications.error'),
        description: t('errors.errorDeletingDocument'),
        ...toastCommonProps,
      });
    }
  };

  if (
    !shouldRenderDeleteButton(
      document,
      currentUser,
      isNewTemplateUpgradeEnabled,
    )
  ) {
    return null;
  }

  return (
    <>
      {renderClickableItem(() => {
        setDisabled(true);
        setIsDeleteModalOpen(true);
      })}

      <DeleteEntityModal
        isDeletingEntity={isDeletingDoc}
        isOpen={isDeleteModalOpen}
        onCloseModal={onClose}
        deleteHandler={handleDelete}
        isDestructive={false}
        modalText={modalText}
        entityName={'document'}
      />
    </>
  );
};
